import React, { useState } from 'react';
import { Briefcase, Users, Rocket, Heart, MapPin, Clock, Send, CheckCircle, Star, Coffee, Zap, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const Career = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    linkedin: '',
    portfolio: '',
    salary: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Career application submitted:', formData);
    toast({
      title: "Başvurunuz Alındı! 🎉",
      description: "İK ekibimiz başvurunuzu inceleyecek ve size en kısa sürede dönüş yapacak.",
    });
    setFormData({ name: '', email: '', phone: '', position: '', experience: '', linkedin: '', portfolio: '', salary: '', message: '' });
  };

  const benefits = [
    { icon: Coffee, title: 'Esnek Çalışma', description: 'Hibrit ve uzaktan çalışma imkanı' },
    { icon: Rocket, title: 'Kariyer Gelişimi', description: 'Eğitim bütçesi ve konferans desteği' },
    { icon: Heart, title: 'Sağlık Sigortası', description: 'Özel sağlık sigortası ve check-up' },
    { icon: Star, title: 'Performans Primi', description: 'Yıllık performans bazlı bonus' },
    { icon: Users, title: 'Harika Ekip', description: 'Tutkulu ve yenilikçi takım arkadaşları' },
    { icon: Zap, title: 'Son Teknoloji', description: 'En güncel araçlar ve teknolojiler' }
  ];

  const openPositions = [
    {
      title: 'Senior React Developer',
      department: 'Yazılım Geliştirme',
      location: 'İstanbul / Uzaktan',
      type: 'Tam Zamanlı',
      description: 'Deneyimli React geliştiricileri arıyoruz. Next.js ve TypeScript bilgisi tercih sebebidir.'
    },
    {
      title: 'UI/UX Designer',
      department: 'Tasarım',
      location: 'İstanbul / Hibrit',
      type: 'Tam Zamanlı',
      description: 'Kullanıcı deneyimi odaklı, Figma ve modern tasarım araçlarında uzman tasarımcılar arıyoruz.'
    },
    {
      title: 'Digital Marketing Specialist',
      department: 'Pazarlama',
      location: 'İstanbul',
      type: 'Tam Zamanlı',
      description: 'SEO, SEM ve sosyal medya pazarlama deneyimi olan uzmanlar arıyoruz.'
    },
    {
      title: 'Mobile Developer (React Native)',
      department: 'Yazılım Geliştirme',
      location: 'Uzaktan',
      type: 'Tam Zamanlı',
      description: 'iOS ve Android için React Native ile uygulama geliştirme deneyimi olan geliştiriciler arıyoruz.'
    },
    {
      title: 'AI/ML Engineer',
      department: 'Yapay Zeka',
      location: 'İstanbul / Uzaktan',
      type: 'Tam Zamanlı',
      description: 'Python, TensorFlow/PyTorch deneyimi olan AI mühendisleri arıyoruz.'
    },
    {
      title: 'Project Manager',
      department: 'Yönetim',
      location: 'İstanbul',
      type: 'Tam Zamanlı',
      description: 'Agile metodolojileri ve proje yönetimi deneyimi olan yöneticiler arıyoruz.'
    }
  ];

  const values = [
    { title: 'İnovasyon', description: 'Sürekli öğrenme ve yenilik peşindeyiz' },
    { title: 'Şeffaflık', description: 'Açık iletişim ve dürüstlük temelimiz' },
    { title: 'İşbirliği', description: 'Birlikte başarıyoruz' },
    { title: 'Tutku', description: 'İşimizi seviyoruz' }
  ];

  const experienceOptions = [
    'Yeni Mezun',
    '1-2 Yıl',
    '3-5 Yıl',
    '5-10 Yıl',
    '10+ Yıl'
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <Briefcase className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">Kariyer</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Geleceği Birlikte
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Şekillendirelim</span>
            </h1>

            <p className="text-xl text-gray-400 mb-8 leading-relaxed">
              RUYAN'da tutkulu, yenilikçi ve sonuç odaklı ekip arkadaşları arıyoruz. 
              Teknolojinin gücüyle işletmeleri dönüştüren bir ekibin parçası olun.
            </p>

            <Button
              onClick={() => document.getElementById('open-positions')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg"
            >
              Açık Pozisyonları Gör
            </Button>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Değerlerimiz</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                <p className="text-gray-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Neden <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">RUYAN</span>?
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Çalışanlarımızın mutluluğu ve gelişimi bizim için öncelik.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/50 transition-all hover:-translate-y-2"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="open-positions" className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Açık <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Pozisyonlar</span>
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Yeteneklerini RUYAN'da keşfet
          </p>

          <div className="grid gap-6">
            {openPositions.map((position, index) => (
              <div 
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/50 transition-all group"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">
                        {position.title}
                      </h3>
                      <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded-full">
                        {position.department}
                      </span>
                    </div>
                    <p className="text-gray-400 mb-3">{position.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {position.location}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {position.type}
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      setFormData({ ...formData, position: position.title });
                      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-6"
                  >
                    Başvur
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-slate-800/60 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold text-white mb-4">
                Başvuru
                <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Formu</span>
              </h2>
              <p className="text-gray-400">
                Kariyer yolculuğunuza RUYAN'da başlayın
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 block">Adınız Soyadınız *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="Ahmet Yılmaz"
                    data-testid="career-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">E-posta *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="ahmet@email.com"
                    data-testid="career-email-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-gray-300 mb-2 block">Telefon *</Label>
                  <Input
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="+90 XXX XXX XX XX"
                    data-testid="career-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="position" className="text-gray-300 mb-2 block">Başvurulan Pozisyon *</Label>
                  <select
                    id="position"
                    required
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-600 text-white h-12 rounded-xl px-4 focus:border-cyan-500"
                    data-testid="career-position-select"
                  >
                    <option value="">Pozisyon Seçin</option>
                    {openPositions.map((pos, index) => (
                      <option key={index} value={pos.title}>{pos.title}</option>
                    ))}
                    <option value="other">Diğer / Genel Başvuru</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="experience" className="text-gray-300 mb-2 block">Deneyim *</Label>
                  <select
                    id="experience"
                    required
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-600 text-white h-12 rounded-xl px-4 focus:border-cyan-500"
                    data-testid="career-experience-select"
                  >
                    <option value="">Deneyim Süresi</option>
                    {experienceOptions.map((exp, index) => (
                      <option key={index} value={exp}>{exp}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="salary" className="text-gray-300 mb-2 block">Maaş Beklentisi (Net)</Label>
                  <Input
                    id="salary"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="Örn: 50.000 ₺"
                    data-testid="career-salary-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="linkedin" className="text-gray-300 mb-2 block">LinkedIn Profili</Label>
                  <Input
                    id="linkedin"
                    value={formData.linkedin}
                    onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="linkedin.com/in/kullaniciadi"
                    data-testid="career-linkedin-input"
                  />
                </div>
                <div>
                  <Label htmlFor="portfolio" className="text-gray-300 mb-2 block">Portfolio / GitHub</Label>
                  <Input
                    id="portfolio"
                    value={formData.portfolio}
                    onChange={(e) => setFormData({ ...formData, portfolio: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="github.com/kullaniciadi"
                    data-testid="career-portfolio-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-300 mb-2 block">Kendinizi Tanıtın *</Label>
                <Textarea
                  id="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-slate-900/50 border-slate-600 text-white rounded-xl resize-none focus:border-cyan-500"
                  placeholder="Deneyimleriniz, projeleriniz ve neden RUYAN'da çalışmak istediğiniz hakkında bilgi verin..."
                  data-testid="career-message-textarea"
                />
              </div>

              <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/50">
                <p className="text-gray-400 text-sm">
                  📎 CV ve portfolyo dosyalarınızı <strong className="text-cyan-400">kariyer@ruyan.com.tr</strong> adresine 
                  başvuru numaranızla birlikte gönderebilirsiniz.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 text-lg font-semibold rounded-xl hover:scale-105 transition-all"
                data-testid="career-submit-button"
              >
                Başvuruyu Gönder
                <Send className="w-5 h-5 ml-2" />
              </Button>

              <p className="text-center text-sm text-gray-500">
                Başvurunuz KVKK kapsamında değerlendirilecektir.
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Career;
